<script setup lang="ts">
import { User, Lock, Refresh, CircleCheck } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'
import { reactive, ref } from 'vue'
import { userLoginApi, userTokenApi, userRegisterApi, userQuickLoginApi, userCheckFormDataApi } from '@/api/user'
import type { UserApiRes } from '@/api/user/types'
import { useUserStore } from '@/stores/modules/user'
import { useCommonStore } from '@/stores/modules/common'
import { initSocket } from '@/utils/web-socket/index'
import { onMounted } from 'vue'
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import { md5 } from 'js-md5'
import { tipList } from './data'
import { shuffleArray } from '@/utils'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()
const commonStore = useCommonStore()

const curTab = ref('login')
const isLogin = ref(false)

const loginFormRef = ref<FormInstance>()
const loginForm = reactive({
  username: '',
  password: '',
})
const loginFormRules = {
  username: [
    { required: true, message: '请输入' },
    { min: 4, max: 32, message: '长度在4~32', trigger: 'blur' },
    {
      pattern: /^(?=[a-zA-Z0-9]*$)([a-zA-Z0-9]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/,
      message: '用户名为字母数字组合或填入邮箱',
      trigger: 'blur',
    },
  ],
  password: [
    { required: true, message: '请输入' },
    { min: 6, max: 32, message: '长度在6~32', trigger: 'blur' },
  ],
}
function handleLogin() {
  loginFormRef.value?.validate(async (valid) => {
    if (!valid) return
    const res = await userLoginApi({
      ...loginForm,
      password: md5(loginForm.password),
    })
    handleUserEnter(res)
  })
}

const registerFormRef = ref<FormInstance>()
const registerForm = reactive({
  username: '',
  nickname: '',
  email: '',
  vCode: '',
  password: '',
  checkPassword: '',
  key: '',
})
const registerFormRules: FormRules = {
  ...loginFormRules,
  username: [
    { required: true, message: '请输入' },
    { min: 4, max: 16, message: '长度在4~16', trigger: 'blur' },
    { pattern: /^[A-Za-z0-9]+$/, message: '仅支持数字及字母组合', trigger: 'blur' },
  ],
  nickname: [
    { required: true, message: '请输入' },
    { min: 2, max: 8, message: '长度在2~8', trigger: 'blur' },
    { pattern: /^[\u4e00-\u9fa5a-zA-Z0-9]+$/, message: '昵称只允许中英文及数字', trigger: 'blur' },
  ],
  checkPassword: [
    { required: true, message: '请输入' },
    {
      validator: (_, value, callback) => {
        if (value !== registerForm.password) {
          callback(new Error('与密码不一致'))
        }
        callback()
      },
      trigger: 'change',
    },
  ],
  key: [
    { required: envMode === 'test' ? true : false, message: '请输入' },
    { min: 36, max: 36, message: '长度为36位', trigger: 'blur' },
  ],
}

function handleRegister() {
  registerFormRef.value?.validate(async (valid) => {
    if (!valid) return
    await userCheckFormDataApi({ username: registerForm.username, nickname: registerForm.nickname })
    const res = await userRegisterApi({
      ...registerForm,
      password: md5(registerForm.password),
    })
    handleUserEnter(res)
  })
}

function handleQuickLogin() {
  ElMessageBox.prompt('请设定您的昵称', '游客登录', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputPattern: /^[\u4e00-\u9fa5a-zA-Z0-9]{2,8}$/,
    inputErrorMessage: '昵称在2~8字，且只允许中英文及数字',
    confirmButtonClass: 'is-plain',
    async beforeClose(action, instance, done) {
      if (action !== 'confirm') return done()
      try {
        const value = instance.inputValue
        instance.confirmButtonLoading = true
        instance.confirmButtonText = '加载中...'
        await userCheckFormDataApi({ username: new Date().getTime().toString(), nickname: value })
        const { token, nickname, username, password } = await userQuickLoginApi({ nickname: value })
        ElMessageBox.alert(`账号已注册并登录，当您更换设备时，需以下凭证，建议截图保存<br />账号：${username}<br />密码：${password}`, '登录成功', {
          dangerouslyUseHTMLString: true,
        })
          .then(() => handleUserEnter({ token, nickname }))
          .catch(() => {})
        done()
      } finally {
        instance.confirmButtonLoading = false
        instance.confirmButtonText = '确定'
      }
    },
  })
}

function handleUserEnter(res: UserApiRes) {
  userStore.token = res.token
  userStore.nickname = res.nickname
  localStorage.setItem(import.meta.env.VITE_TOKEN_NAME, res.token)
  isLogin.value = true
}

async function handleStart() {
  await initSocket()
  const loginUserIdList: number[] = JSON.parse(localStorage.getItem('war-soul-test-login') || '[]')
  // if (loginUserIdList.length >= 10 && !loginUserIdList.includes(userStore.id)) {
  //   return ElMessageBox.alert('请勿注册/登录多个账号，行为严重可能会导致您的账号被封禁', '温馨提示', {
  //     icon: 'warning',
  //     confirmButtonText: '确定',
  //     callback: () => {
  //       localStorage.removeItem(import.meta.env.VITE_TOKEN_NAME)
  //       location.reload()
  //     },
  //   })
  // }
  loginUserIdList.push(userStore.id)
  localStorage.setItem('war-soul-test-login', JSON.stringify([...new Set(loginUserIdList)]))
  commonStore.isSocketInit = true
  commonStore.path = 'Home'
}
function handleLogout() {
  ElMessageBox.confirm('确认退出登录？【请务必确认您知晓该账号的用户名及密码】', '提示', {
    type: 'warning',
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      localStorage.removeItem(import.meta.env.VITE_TOKEN_NAME)
      location.reload()
    })
    .catch(() => {})
}

const tipIndex = ref(0)
function nextTip() {
  tipIndex.value++
  if (tipIndex.value === tipList.length) {
    tipIndex.value = 0
  }
}

onMounted(async () => {
  if (localStorage.getItem(import.meta.env.VITE_TOKEN_NAME)) {
    const res = await userTokenApi()
    userStore.nickname = res.nickname
    isLogin.value = true
  }
  shuffleArray(tipList)
})
</script>

<template>
  <div class="page start-page" v-bind="$attrs" :style="{ paddingTop: '20vh' }">
    <!-- <div class="tips" v-show="envMode === 'test'">
      <h2>重要提醒</h2>
      <p>正式服将于2024-08-25上线，上线后您将无法通过Tap登录测试服，请使用Tap游玩的玩家务必加入官方Q群安装其他登录渠道（APP端、网页端无影响）。</p>
      <p>正式服上线后将暂停测试服的注册（后续玩家过少时会招募），并关闭测试服的赞助渠道。</p>
      <p>正式服已上线，该版本已关闭注册，您可以通过下方按钮【前往正式服】</p>
      <p>在【主城】中的【设置】界面，您可以获取您的【唯一标识】，用于在正式服领奖，<b style="color: #f56c6c">请勿泄露您的唯一标识</b>。</p>
    </div> -->
    <h1>战魂觉醒OL</h1>
    <div class="account" v-show="!isLogin">
      <el-tabs v-model="curTab" stretch>
        <el-tab-pane label="登录" name="login">
          <el-form ref="loginFormRef" :model="loginForm" :rules="loginFormRules" v-loading="commonStore.loading">
            <el-form-item prop="username">
              <el-input v-model="loginForm.username" :prefix-icon="User" placeholder="请输入用户名或邮箱"></el-input>
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                :prefix-icon="Lock"
                placeholder="请输入密码"
                @keydown.enter="handleLogin"
              ></el-input>
            </el-form-item>
            <el-form-item>
              <div style="width: 100%; text-align: center">
                <el-button type="primary" plain style="width: 80%; margin-bottom: 10px" @click="handleLogin">登录</el-button>
                <template v-if="envMode !== 'test'">
                  <br />
                  <el-button plain style="width: 80%; margin-bottom: 10px" @click="handleQuickLogin" v-if="envMode !== 'test'">游客登录</el-button>
                </template>
                <template v-if="envMode === 'test'">
                  <br />
                  <el-button type="success" plain style="width: 80%" tag="a" href="https://aring.cc/awakening-of-war-soul-ol/">前往正式服</el-button>
                </template>
              </div>
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="注册" name="register">
          <el-form ref="registerFormRef" :model="registerForm" :rules="registerFormRules" v-loading="commonStore.loading">
            <el-form-item prop="username">
              <el-input v-model="registerForm.username" :prefix-icon="User" placeholder="请输入用户名（用于登录）"></el-input>
            </el-form-item>
            <el-form-item prop="nickname">
              <el-input v-model="registerForm.nickname" :prefix-icon="User" placeholder="请输入昵称"></el-input>
            </el-form-item>
            <!-- <el-form-item prop="email">
              <el-input v-model="registerForm.email" :prefix-icon="Message" placeholder="请输入邮箱">
                <template #append>
                  <el-button>发送验证码</el-button>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item prop="vCode">
              <el-input v-model="registerForm.vCode" type="password" :prefix-icon="CircleCheck" placeholder="请输入验证码"></el-input>
            </el-form-item> -->
            <el-form-item prop="password">
              <el-input v-model="registerForm.password" type="password" :prefix-icon="Lock" placeholder="请输入密码"></el-input>
            </el-form-item>
            <el-form-item prop="checkPassword">
              <el-input v-model="registerForm.checkPassword" type="password" :prefix-icon="Lock" placeholder="请确认密码"></el-input>
            </el-form-item>
            <el-form-item prop="key" v-if="envMode === 'test'">
              <el-input v-model="registerForm.key" :prefix-icon="CircleCheck" placeholder="请输入测试服激活码"></el-input>
            </el-form-item>
            <el-form-item>
              <div style="width: 100%; text-align: center">
                <el-button type="primary" plain style="width: 80%" @click="handleRegister">注册</el-button>
              </div>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div class="start" v-show="isLogin">
      <h2>欢迎您，{{ userStore.nickname }}</h2>
      <CommonBtn plain style="width: 100%; margin-bottom: 10px" @done="handleStart">进入游戏</CommonBtn>
      <CommonBtn type="" plain style="width: 100%" @done="handleLogout">切换账号</CommonBtn>
    </div>
    <p class="affix small-tips" v-show="isLogin">
      <span>✨小提示：<span v-html="tipList[tipIndex]"></span></span>
      <a href="javascript:;" @click="nextTip">
        <el-icon><Refresh /></el-icon>换一个
      </a>
    </p>

    <p style="position: fixed; left: 0; bottom: 10px; text-align: center; width: 100%">{{ envMode === 'test' ? '【不删档测试】' : '【正式服】' }}</p>
  </div>
</template>

<style lang="scss" scoped>
.start-page {
  .tips {
    h2 {
      font-size: 16px;
    }
    p {
      font-size: 14px;
      margin: 8px 0;
    }
  }
  .account,
  .start {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
  .small-tips {
    margin-top: 40px;
    font-size: 14px;
    display: flex;
    justify-content: space-between;
    a {
      line-height: 20px;
      min-width: 56px;
      margin-left: 5px;
    }
  }
}
</style>
